import { useEffect, useState } from "react";

// REST siblings of the SSE stream (use-track-event-stream.ts) — same
// /collect/v1 base. Plain fetch, not the app's useRequest/useCrmApi wrapper:
// these are public, write-key-authenticated endpoints (not the app's own
// bearer session), so there's nothing to gain from routing through it and,
// per the stream's own CORS lesson, real value in keeping custom headers out.
const TRACK_COLLECT_BASE = process.env.REACT_APP_TRACK_API_URL || "https://journeybuilder.connect.dalmlabs.com/collect/v1";

export interface TrackStreamStats {
  events_last_hour: number;
  events_per_second: number;
  active_sources: number;
  total: number;
}

// One-shot backfill — GET /stream/recent. The live SSE only pushes events
// from the moment it's connected, so this is what fills in everything that
// fired before that (or during a dropped-connection gap). Call on mount and
// again on every reconnect by changing `refreshKey`.
export function useTrackRecentEvents(writeKey: string, limit: number, refreshKey: unknown) {
  const [data, setData] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${TRACK_COLLECT_BASE}/stream/recent?write_key=${encodeURIComponent(writeKey)}&limit=${limit}`);
        const json = await res.json();
        if (!cancelled) setData(Array.isArray(json?.events) ? json.events : []);
      } catch {
        if (!cancelled) setData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [writeKey, limit, refreshKey]);

  return { data, loading };
}

// Polls GET /stream/stats every `intervalMs` while `enabled` — the real
// aggregate numbers for the metric cards, not a client-computed estimate.
export function useTrackStreamStats(enabled: boolean, writeKey: string, intervalMs = 5000) {
  const [stats, setStats] = useState<TrackStreamStats | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${TRACK_COLLECT_BASE}/stream/stats?write_key=${encodeURIComponent(writeKey)}`);
        const json = await res.json();
        if (!cancelled && res.ok) setStats(json);
      } catch {
        // keep the last known stats on a transient failure
      }
    };
    load();
    const t = setInterval(load, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [enabled, writeKey, intervalMs]);

  return stats;
}

// The Meridian test tenant's write key — same one the live-event-stream
// page uses. Confirmed by the backend team: the per-tenant snippet endpoint
// (GET /workspaces/{id}/snippet) ONLY ever accepts one specific admin key —
// not the login token, not any tenant/org header — because there's no
// tenant_id concept in this system at all; the write key IS the identifier.
// So the endpoint can never work from this app's session, by design, and
// isn't worth calling. There's also nothing behind it beyond this exact
// static text with the write key filled in — confirmed by backend as
// "that IS the snippet" — so it's built client-side instead of fetched.
export const MERIDIAN_TRACK_WRITE_KEY = process.env.REACT_APP_TRACK_WRITE_KEY || "pk_live_JRur9XyWQNiD7rfKAmaR9MAOUXNEQM";

// Confirmed real script + init call (2026-09-24) — the earlier
// cdn.dalm.io/sdk.js guess never existed (404 in the browser); this is the
// actual hosted SDK file and load API.
export const SDK_SCRIPT_URL = "https://journeybuilder.connect.dalmlabs.com/collect/v1/sdk/dalm.js";
export const SDK_API_HOST = "https://journeybuilder.connect.dalmlabs.com/collect";

export function buildInstallSnippet(writeKey: string): string {
  return `<script src="${SDK_SCRIPT_URL}"></script>
<script>
  window.dalmSDK.load("${writeKey}", {
    apiHost: "${SDK_API_HOST}",
    consent: "granted"
  });
</script>`;
}

// Fires one real event — POST /collect/v1/events. Used by the setup guide's
// "Send test event" button, which used to just flip local state with no
// actual request.
export async function fireTrackTestEvent(
  writeKey: string,
  eventName: string,
  anonymousId: string
): Promise<{ ok: boolean; detail?: string }> {
  try {
    const res = await fetch(`${TRACK_COLLECT_BASE}/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${writeKey}` },
      body: JSON.stringify({
        batch: [
          {
            event_id: crypto.randomUUID(),
            anonymous_id: anonymousId,
            type: "track",
            event: eventName,
            occurred_at: new Date().toISOString(),
            properties: {},
          },
        ],
        sent_at: new Date().toISOString(),
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.status === 202 || (res.ok && json?.accepted)) return { ok: true };
    return { ok: false, detail: json?.detail || json?.errors?.[0] || `Request failed (${res.status})` };
  } catch (err) {
    return { ok: false, detail: err instanceof Error ? err.message : "Network error" };
  }
}
